import { useEffect, useState } from "react";

/** repository watch 형태 — (값 콜백, 오류 콜백) → 구독 해제 */
export type ScopedSubscribe<T> = (cb: (v: T) => void, onError: (e: Error) => void) => () => void;

/**
 * 범위(법인·사업부)가 바뀔 수 있는 구독 — 설정 문서 훅 공용(12 §9-3).
 *
 * 상태에 범위 키를 함께 담고, 지금 키와 다르면 initial 을 돌려준다(경원으로 바꾼 순간 중앙 값이 한 프레임도 보이지 않게).
 * effect 본문에서 setState 하지 않는다 — 콜백 안에서만.
 *
 * @param key       범위 키(보통 settingsDocPath 결과). 바뀌면 다시 구독
 * @param subscribe useCallback 으로 고정한 구독 함수(key 와 같은 의존성)
 * @param initial   값이 아직 없거나 오류일 때 돌려줄 기본값(렌더마다 같은 값이어야 함 — 모듈 상수 권장)
 */
export function useScopedWatch<T>(
  key: string,
  subscribe: ScopedSubscribe<T>,
  initial: T,
): { value: T; loaded: boolean; error: Error | null } {
  const [state, setState] = useState<
    { key: string; has: true; value: T } | { key: string; has: false; error: Error } | null
  >(null);

  useEffect(() => {
    return subscribe(
      (v) => setState({ key, has: true, value: v }),
      (e) => setState({ key, has: false, error: e }),
    );
  }, [key, subscribe]);

  if (!state || state.key !== key) return { value: initial, loaded: false, error: null };
  if (state.has) return { value: state.value, loaded: true, error: null };
  return { value: initial, loaded: true, error: state.error };
}
